import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface ExchangeItem {
  id: string;
  gift_item_name: string;
  points_spent: number;
  status: string;
  gift_image_url: string | null;
  created_at: string;
  completed_at: string | null;
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

const statusBadge = (status: string) => {
  switch (status) {
    case "completed": return { bg: "bg-accent-100", text: "text-accent-700", label: "교환완료", icon: "ri-check-line" };
    case "cancelled": return { bg: "bg-red-100", text: "text-red-600", label: "취소됨", icon: "ri-close-line" };
    default: return { bg: "bg-yellow-100", text: "text-yellow-700", label: "처리중", icon: "ri-time-line" };
  }
};

export default function ExchangeHistory() {
  const { user, loading: authLoading } = useAuth();
  const [exchanges, setExchanges] = useState<ExchangeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const fetchExchanges = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError("");

    try {
      const { data, error: fetchError } = await supabase
        .from("gift_exchanges")
        .select("id, gift_item_name, points_spent, status, gift_image_url, created_at, completed_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      setExchanges(data || []);
    } catch {
      setError("교환 내역을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchExchanges();
    }
  }, [user, fetchExchanges]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-4 h-4 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          <span className="text-sm">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-lock-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-sm text-foreground-600 mb-4">교환 내역은 로그인 후 확인할 수 있습니다</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            로그인하러 가기
          </Link>
        </div>
      </div>
    );
  }

  const totalSpent = exchanges.reduce((sum, e) => sum + e.points_spent, 0);
  const completedCount = exchanges.filter((e) => e.status === "completed").length;
  const pendingCount = exchanges.filter((e) => e.status === "pending").length;

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/profile"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-foreground-700">내 교환내역</h1>
            <p className="text-xs text-foreground-600 mt-0.5">교환 신청한 기프티콘을 확인하세요</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-background-100 rounded-xl border border-background-300 p-4 text-center">
            <p className="text-[10px] text-foreground-600 mb-1">전체</p>
            <p className="text-xl font-black text-foreground-700">{exchanges.length}</p>
            <p className="text-[10px] text-foreground-600">건</p>
          </div>
          <div className="bg-background-100 rounded-xl border border-background-300 p-4 text-center">
            <p className="text-[10px] text-foreground-600 mb-1">처리중</p>
            <p className="text-xl font-black text-yellow-500">{pendingCount}</p>
            <p className="text-[10px] text-foreground-600">건</p>
          </div>
          <div className="bg-background-100 rounded-xl border border-background-300 p-4 text-center">
            <p className="text-[10px] text-foreground-600 mb-1">사용 포인트</p>
            <p className="text-xl font-black text-accent-500">{totalSpent.toLocaleString()}</p>
            <p className="text-[10px] text-foreground-600">P</p>
          </div>
        </div>

        {/* Exchange List */}
        <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-background-300 flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                <i className="ri-exchange-line" />
              </span>
              교환 내역
            </h2>
            <span className="text-[10px] text-foreground-600">{exchanges.length}건</span>
          </div>

          {loading ? (
            <div className="px-5 md:px-6 py-10 flex items-center justify-center">
              <div className="flex items-center gap-2 text-foreground-600">
                <span className="w-4 h-4 flex items-center justify-center animate-spin">
                  <i className="ri-loader-4-line" />
                </span>
                <span className="text-sm">불러오는 중...</span>
              </div>
            </div>
          ) : error ? (
            <div className="px-5 md:px-6 py-10 text-center">
              <p className="text-sm text-red-400 mb-3">{error}</p>
              <button
                onClick={fetchExchanges}
                className="px-4 py-2 bg-background-200 text-foreground-600 text-xs rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                다시 시도
              </button>
            </div>
          ) : exchanges.length === 0 ? (
            <div className="px-5 md:px-6 py-10 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                  <i className="ri-gift-line" />
                </span>
              </div>
              <p className="text-sm text-foreground-600 mb-1">아직 교환 내역이 없습니다</p>
              <p className="text-xs text-foreground-600 mb-3">포인트존에서 기프티콘을 교환해보세요</p>
              <Link
                to="/guide/gift"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 text-xs font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line" />
                </span>
                기프티콘 교환하러 가기
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-background-300">
              {exchanges.map((exchange) => {
                const badge = statusBadge(exchange.status);
                return (
                  <div
                    key={exchange.id}
                    className="flex items-center gap-4 px-5 md:px-6 py-4"
                  >
                    <div className="w-12 h-12 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {exchange.status === "completed" && exchange.gift_image_url ? (
                        <img
                          src={exchange.gift_image_url}
                          alt={exchange.gift_item_name}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setSelectedImage(exchange.gift_image_url)}
                        />
                      ) : (
                        <span className={`w-5 h-5 flex items-center justify-center ${exchange.status === "cancelled" ? "text-red-400" : "text-accent-500"}`}>
                          <i className={exchange.status === "completed" ? "ri-gift-2-fill" : exchange.status === "cancelled" ? "ri-close-circle-fill" : "ri-time-fill"} />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground-700 truncate">
                          {exchange.gift_item_name}
                        </p>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full flex-shrink-0 ${badge.bg} ${badge.text}`}>
                          <span className="w-2.5 h-2.5 flex items-center justify-center">
                            <i className={badge.icon} />
                          </span>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-[10px] text-foreground-600 mt-0.5">
                        신청: {formatDateTime(exchange.created_at)}
                        {exchange.completed_at && exchange.status === "completed" && (
                          <span className="ml-2">· 완료: {formatDateTime(exchange.completed_at)}</span>
                        )}
                      </p>
                      {exchange.status === "completed" && exchange.gift_image_url && (
                        <button
                          onClick={() => setSelectedImage(exchange.gift_image_url)}
                          className="mt-1.5 text-[10px] text-primary-500 hover:text-primary-400 transition-colors cursor-pointer flex items-center gap-1"
                        >
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-eye-line" />
                          </span>
                          기프티콘 이미지 보기
                        </button>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-secondary-500">
                        -{exchange.points_spent.toLocaleString()}P
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelectedImage(null)} />
          <div className="relative bg-background-100 rounded-xl border border-background-300 shadow-xl w-full max-w-lg overflow-hidden z-10">
            <div className="px-5 py-4 border-b border-background-300 flex items-center justify-between">
              <h3 className="text-sm font-bold text-foreground-800">기프티콘 이미지</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-600" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center">
              <img
                src={selectedImage}
                alt="기프티콘"
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            </div>
            <div className="px-5 py-3 border-t border-background-300 flex justify-end gap-2">
              <a
                href={selectedImage}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="px-4 py-2 text-xs font-bold bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-download-line mr-1" />
                이미지 저장
              </a>
              <button
                onClick={() => setSelectedImage(null)}
                className="px-4 py-2 text-xs text-foreground-600 hover:text-foreground-800 bg-background-200 rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}